import { type LocaleDefinition } from '@solinkcorp/locale';
import moment from 'moment';
import React, { forwardRef } from 'react';
import TimeFormatter from 'utils/TimeFormatter';

interface CustomDateTimeInputProps {
  locale: LocaleDefinition;
  showTime: boolean;
  timezoneId?: string;
  value?: Date | number;
  onClick?: () => void;
  dataTestId?: string;
}

/**
 * A custom date time input that renders as a clickable text of the select date and time.
 * In format like "Jan, 16 2024 11:07 AM PST"
 */
const CustomDateTimeInput: React.FC<CustomDateTimeInputProps> = forwardRef(
  (props: CustomDateTimeInputProps, ref: React.Ref<HTMLDivElement> | null) => {
    const { value, onClick, locale, showTime, timezoneId = '', dataTestId } = props;
    if (value === undefined) {
      return null;
    }

    const timeFormat = TimeFormatter.getString({ zeroPrefix: false, seconds: false });
    const date = moment.tz(value, timezoneId);
    const isToday = moment().isSame(date, 'day');

    let dateFormat = 'll z';
    if (!showTime && isToday) {
      dateFormat = 'll z';
    } else if (showTime && !isToday) {
      dateFormat = `ll ${timeFormat}`;
    } else if (showTime && isToday) {
      dateFormat = timeFormat;
    }

    let displayDate = date.format(dateFormat);
    if (isToday) {
      displayDate = `${locale.date.today}, ${displayDate}`;
    }

    return (
      <div
        className="date-display"
        data-testid={dataTestId ?? 'date-picker-text'}
        onClick={onClick}
        ref={ref}
      >
        {displayDate}
      </div>
    );
  },
);

CustomDateTimeInput.displayName = 'CustomDateTimeInput';

export default CustomDateTimeInput;
